import React, { FunctionComponent } from "react";
import Image, { StaticImageData } from "next/image";
import isString from "lodash/isString";
import { Asset } from "contentful";
import { Utils } from "@/src/Utils";
import { ImageProps } from "next/image";

interface StaticRequire {
    default: StaticImageData;
}

declare type StaticImport = StaticRequire | StaticImageData;

export type ImageSource = string | StaticImport | Asset;
export type ImageOutput = string | StaticImport;

interface ImageInfo {
    image: ImageOutput;
    type?: string;
    key: string;
    blurDataURL?: string;
    alt?: string;
    width?: number;
    height?: number;
}

const MAX_IMAGE_PIXELS = 2048;

function clampSize(imageWidth: number, imageHeight: number): [number, number] {
    const aspect = imageWidth / imageHeight;

    if (imageWidth > MAX_IMAGE_PIXELS) {
        imageWidth = MAX_IMAGE_PIXELS;
        imageHeight = imageWidth / aspect;
    }

    if (imageHeight > MAX_IMAGE_PIXELS) {
        const aspect = imageWidth / imageHeight;
        imageHeight = MAX_IMAGE_PIXELS;
        imageWidth = imageHeight * aspect;
    }

    return [imageWidth, imageHeight];
}

export interface SmartImageProps extends Partial<Pick<ImageProps, "fill" | "priority">> {
    image: ImageSource;
    mobileViewWidthPercent?: number;
    desktopViewWidthPercent?: number;
    /**
     * A full `sizes` attribute, replacing the one built from the view-width percentages. Use it when the image sits
     * in a fixed-size slot (e.g. a 160px tile), where a vw value makes the browser download a far larger source.
     */
    sizes?: string;
    /**
     * Overrides the automatic behaviour, which is to treat the image as decorative (`alt=""`) only when no alt text
     * can be resolved — either from the `alt` prop or, for a Contentful Asset, its `description` field.
     * Set to `true` to force `alt=""` on an ornamental image whose source happens to carry a description.
     */
    isDecorative?: boolean;
    alt?: string;
    className?: string;

    width?: number | `${number}`;
    height?: number | `${number}`;

    title?: string;
    style?: React.CSSProperties;
    objectFit?: React.CSSProperties["objectFit"];
    objectPosition?: React.CSSProperties["objectPosition"];
    itemProp?: string;
}

/**
 * Take an input image of string, Asset, or StaticImageData and try to extract all the information we can from it.
 */
function extractImageInfo(inputImage: ImageSource, altOverride?: string): ImageInfo {
    let outputImage: ImageSource;
    let imageKey: string;
    let imageType: string | undefined;
    let blurDataURL: string | undefined;
    let alt: string | undefined;
    let imageWidth: number | undefined;
    let imageHeight: number | undefined;

    if (isString(inputImage)) {
        // This is a URL to an image
        const imageData = Utils.normalizeURL(inputImage);
        outputImage = imageData.normalizedURL;
        imageKey = inputImage as string;
        imageType = Utils.extractFileExtension(inputImage);

        if (imageData.isExternal) {
            blurDataURL = Utils.getPlaceholderImageURL(imageData.normalizedURL);
        }
    } else if ((inputImage as Asset).fields?.file?.url) {
        // This is an image in our CMS
        outputImage = (inputImage as Asset).fields.file.url;

        if (!outputImage.startsWith("http")) {
            outputImage = "https:" + outputImage;
        }

        imageKey = outputImage;
        blurDataURL = Utils.getPlaceholderImageURL(outputImage);
        alt = (inputImage as Asset).fields.description;
        imageWidth = (inputImage as Asset).fields.file.details.image?.width;
        imageHeight = (inputImage as Asset).fields.file.details.image?.height;
        imageType = (inputImage as Asset).fields.file.contentType;
    } else {
        // This is a StaticImageData
        const staticImage = (inputImage as StaticRequire).default ? (inputImage as StaticRequire).default : (inputImage as StaticImageData);
        outputImage = staticImage;
        imageKey = staticImage.src;
        imageWidth = staticImage.width;
        imageHeight = staticImage.height;
        imageType = Utils.extractFileExtension(staticImage.src);
    }

    return {
        image: outputImage,
        type: imageType,
        key: imageKey,
        blurDataURL: blurDataURL,
        alt: altOverride ?? alt,
        width: imageWidth,
        height: imageHeight
    };
}

export const SmartImage: FunctionComponent<SmartImageProps> = props => {
    if (!props.image) {
        return null;
    }

    const imageInfo = extractImageInfo(props.image, props.alt);

    // Default to decorative only when we have nothing meaningful to say about the image.
    // An explicit `isDecorative` always wins, so background/ornamental images can opt back out.
    const isDecorative = props.isDecorative ?? !imageInfo.alt;

    if (!isDecorative && !imageInfo.alt) {
        console.warn("Non-decorative images require a valid alt tag.");
    }

    const isFillMode = props.fill ?? false;

    // Use width and height if not in fill mode
    const width = !isFillMode ? props.width ?? imageInfo.width : undefined;
    const height = !isFillMode ? props.height ?? imageInfo.height : undefined;

    // Determine styles
    const styles: React.CSSProperties = {
        ...props.style,
        maxWidth: "100%",
        // Only set height when not in fill mode
        ...(isFillMode ? {} : { height: "auto" })
    };

    if (isFillMode) {
        styles.position = "absolute";
        styles.top = 0;
        styles.left = 0;
        styles.bottom = 0;
        styles.right = 0;
        styles.objectFit = props.objectFit ?? "cover";
        styles.objectPosition = props.objectPosition ?? "center";
    }

    if (imageInfo.width && imageInfo.height) {
        [imageInfo.width, imageInfo.height] = clampSize(imageInfo.width, imageInfo.height);
    }

    const disableOptimize = !!imageInfo.type && (imageInfo.type.includes("gif") || imageInfo.type.includes("svg"));
    const isRaster = !!imageInfo.type && !imageInfo.type.includes("svg");

    return (
        <Image
            className={props.className}
            key={imageInfo.key}
            src={imageInfo.image}
            priority={props.priority}
            // Next 15's `priority` preloads + eager-loads but no longer sets fetchpriority,
            // leaving LCP hero images at fetchpriority=auto (flagged by Semrush, ENG QW-3).
            fetchPriority={props.priority ? "high" : undefined}
            width={width}
            height={height}
            title={props.title}
            alt={isDecorative ? "" : imageInfo.alt ?? ""}
            fill={isFillMode || undefined}
            blurDataURL={isRaster ? imageInfo.blurDataURL : undefined}
            placeholder={isRaster ? "blur" : undefined}
            unoptimized={disableOptimize}
            sizes={isRaster ? props.sizes ?? `(max-width: 768px) ${props.mobileViewWidthPercent ?? 75}vw, ${props.desktopViewWidthPercent ?? 75}vw` : undefined}
            style={styles}
            itemProp={props.itemProp}
        />
    );
};
